import { MemberCampaign, MemberCampaignPatchDTO } from '@retail/campaign/types';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import {
  useCampaignEnrollmentApi,
  useDistributeCampaign,
  useEditMemberCampaign,
} from '@retail/campaign/data-access';
import { useToast } from '@retail/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useInvalidateCampaignQueries } from './useInvalidateCampaignQueries';

interface Props {
  campaign: MemberCampaign;
}

export const useMemberCampaignDetailsActions = ({ campaign }: Props) => {
  const toast = useToast();
  const t = useCampaignTFunction();
  const queryClient = useQueryClient();
  const { assortmentId } = useSelectedOrgUnit();
  const invalidateCampaignQueries = useInvalidateCampaignQueries({ campaignId: campaign.id });

  const { mutateAsync: mutateEditCampaignAsync, isPending: isLoadingEdit } = useEditMemberCampaign({
    campaignId: campaign.id,
  });
  const { mutateAsync: mutateDistributeCampaign, isPending: isLoadingDistribution } =
    useDistributeCampaign({
      campaignId: campaign.id,
    });
  const { mutateAsync: mutateEnrollCampaign, isPending: isLoadingEnrollCampaign } =
    useCampaignEnrollmentApi({
      assortmentId,
      campaignId: campaign.id,
    });

  const campaignPatchDtoBase: MemberCampaignPatchDTO = useMemo(
    () => ({
      campaignName: campaign.name,
      description: campaign.description,
      validTo: dayjs(campaign.validTo).format('YYYY-MM-DD'),
      validFrom: dayjs(campaign.validFrom).format('YYYY-MM-DD'),
      purchasePeriodTo: dayjs(campaign.purchasePeriodTo).format('YYYY-MM-DD'),
      purchasePeriodFrom: dayjs(campaign.purchasePeriodFrom).format('YYYY-MM-DD'),
    }),
    [campaign]
  );

  const handleChangeCampaign = useCallback(
    async (campaignPatch: MemberCampaignPatchDTO) => {
      await mutateEditCampaignAsync({
        body: campaignPatch,
      })
        .then(() => {
          toast.success(t('campaign.messages.editCampaign'));
          queryClient.invalidateQueries({ queryKey: ['campaigns', campaign.id] });
        })
        .catch((error) => {
          console.error(error);
          toast.error(t('campaign.error.genericError'));
          throw error;
        });
    },
    [campaign.id, mutateEditCampaignAsync, queryClient, t, toast]
  );

  const handleJoinCampaign = useCallback(async () => {
    await mutateEnrollCampaign({ body: { shouldEnroll: true } })
      .then(() => {
        toast.success(t('campaign.messages.joined'));
        queryClient.invalidateQueries({ queryKey: ['campaigns', campaign.id] });
      })
      .catch((error) => {
        console.error(error);
        toast.error(t('campaign.error.genericError'));
      });
  }, [campaign.id, mutateEnrollCampaign, queryClient, t, toast]);

  const handleLeaveCampaign = useCallback(async () => {
    await mutateEnrollCampaign({ body: { shouldEnroll: false } })
      .then(() => {
        toast.success(t('campaign.messages.left'));
        queryClient.invalidateQueries({ queryKey: ['campaigns', campaign.id] });
      })
      .catch((error) => {
        console.error(error);
        toast.error(t('campaign.error.genericError'));
      });
  }, [campaign.id, mutateEnrollCampaign, queryClient, t, toast]);

  const handleChangeCampaignName = useCallback(
    async (name: string) => {
      const previousName = campaign.name;
      campaign.name = name;
      await handleChangeCampaign({ ...campaignPatchDtoBase, campaignName: name }).catch(() => {
        campaign.name = previousName;
      });
    },
    [campaign, campaignPatchDtoBase, handleChangeCampaign]
  );

  const handleChangeDescription = useCallback(
    async (description: string) => {
      const previousDescription = campaign.description;
      campaign.description = description;
      await handleChangeCampaign({ ...campaignPatchDtoBase, description }).catch(() => {
        campaign.description = previousDescription;
      });
    },
    [campaign, campaignPatchDtoBase, handleChangeCampaign]
  );

  const handleChangeCampaignPeriod = useCallback(
    async ({ validTo, validFrom }: Pick<MemberCampaign, 'validTo' | 'validFrom'>) => {
      const previousValidFrom = campaign.validFrom;
      const previousValidTo = campaign.validTo;
      campaign.validFrom = validFrom;
      campaign.validTo = validTo;

      await handleChangeCampaign({
        ...campaignPatchDtoBase,
        validTo: dayjs(validTo).format('YYYY-MM-DD'),
        validFrom: dayjs(validFrom).format('YYYY-MM-DD'),
      }).catch(() => {
        campaign.validTo = previousValidTo;
        campaign.validFrom = previousValidFrom;
      });
    },
    [campaign, campaignPatchDtoBase, handleChangeCampaign]
  );

  const handleChangePurchasePeriod = useCallback(
    async ({
      purchasePeriodTo,
      purchasePeriodFrom,
    }: Pick<MemberCampaign, 'purchasePeriodTo' | 'purchasePeriodFrom'>) => {
      const previousPurchaseFrom = campaign.purchasePeriodFrom;
      const previousPurchaseTo = campaign.purchasePeriodTo;
      campaign.purchasePeriodFrom = purchasePeriodFrom;
      campaign.purchasePeriodTo = purchasePeriodTo;

      await handleChangeCampaign({
        ...campaignPatchDtoBase,
        purchasePeriodTo: dayjs(purchasePeriodTo).format('YYYY-MM-DD'),
        purchasePeriodFrom: dayjs(purchasePeriodFrom).format('YYYY-MM-DD'),
      }).catch(() => {
        campaign.purchasePeriodTo = previousPurchaseTo;
        campaign.purchasePeriodFrom = previousPurchaseFrom;
      });
    },
    [campaign, campaignPatchDtoBase, handleChangeCampaign]
  );

  const handleCampaignDistribution = useCallback(async () => {
    await mutateDistributeCampaign({ urlVariables: campaign.id });
    toast.success(t('campaign.messages.distributed'));
    invalidateCampaignQueries();
  }, [campaign.id, invalidateCampaignQueries, mutateDistributeCampaign, t, toast]);

  return useMemo(
    () => ({
      isLoadingEdit,
      isLoadingDistribution,
      isLoadingEnrollCampaign,
      handleChangeCampaignName,
      handleChangeDescription,
      handleChangeCampaignPeriod,
      handleChangePurchasePeriod,
      handleCampaignDistribution,
      handleJoinCampaign,
      handleLeaveCampaign,
    }),
    [
      isLoadingEdit,
      isLoadingDistribution,
      isLoadingEnrollCampaign,
      handleChangeCampaignName,
      handleChangeDescription,
      handleChangeCampaignPeriod,
      handleChangePurchasePeriod,
      handleCampaignDistribution,
      handleJoinCampaign,
      handleLeaveCampaign,
    ]
  );
};
